import { useState } from "react";
import { motion } from "framer-motion";
import { User, Save, Mail, Phone, Calendar } from "lucide-react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import api from "@/utils/api";
import { toast } from "sonner";

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const [form, setForm] = useState({ name: user?.name || "", phone: user?.phone || "" });
  const [saving, setSaving] = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/auth/profile", { name: form.name, phone: form.phone });
      await refreshUser();
      toast.success("Profile updated successfully!");
    } catch {
      toast.error("Failed to update profile.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-2xl space-y-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 uppercase">Profile</h1>
          <p className="text-slate-500 text-sm mt-0.5">Manage your account information.</p>
        </div>

        {/* Avatar Card */}
        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <div className="flex items-center gap-5">
            <div className="w-16 h-16 rounded-full bg-brand-500 flex items-center justify-center text-white font-heading text-2xl font-bold flex-shrink-0">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div>
              <h2 className="font-heading font-bold text-xl text-slate-800">{user?.name}</h2>
              <p className="text-slate-500 text-sm">{user?.email}</p>
              <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider ${
                user?.role === "admin" ? "bg-purple-100 text-purple-700" :
                user?.role === "agent" ? "bg-orange-100 text-orange-700" :
                "bg-brand-100 text-brand-600"
              }`}>{user?.role}</span>
            </div>
          </div>
        </div>

        {/* Edit Form */}
        <form onSubmit={handleSave} className="bg-white rounded-lg border border-slate-200 p-6 space-y-5">
          <h3 className="font-heading font-semibold text-lg text-slate-800 uppercase">Edit Information</h3>

          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5 flex items-center gap-1">
              <User className="w-3.5 h-3.5" /> Full Name
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="profile-name-input"
              className="w-full px-3 py-2.5 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5 flex items-center gap-1">
              <Mail className="w-3.5 h-3.5" /> Email Address
            </label>
            <input
              type="email"
              value={user?.email}
              disabled
              className="w-full px-3 py-2.5 border border-slate-100 rounded-sm text-sm bg-slate-50 text-slate-400 cursor-not-allowed"
            />
            <p className="text-xs text-slate-400 mt-1">Email cannot be changed.</p>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5 flex items-center gap-1">
              <Phone className="w-3.5 h-3.5" /> Phone Number
            </label>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              data-testid="profile-phone-input"
              placeholder="+91 9876543210"
              className="w-full px-3 py-2.5 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500"
            />
          </div>

          <button
            type="submit"
            disabled={saving}
            data-testid="profile-save-btn"
            className="flex items-center gap-2 px-6 py-2.5 bg-brand-500 text-white font-semibold rounded-sm hover:bg-brand-600 transition-colors disabled:opacity-70 text-sm uppercase"
          >
            <Save className="w-4 h-4" />
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </form>

        {/* Account Info */}
        <div className="bg-slate-50 rounded-lg border border-slate-200 p-5">
          <h3 className="font-semibold text-sm text-slate-600 mb-3 flex items-center gap-2">
            <Calendar className="w-4 h-4 text-brand-500" /> Account Information
          </h3>
          <div className="space-y-2 text-xs text-slate-500">
            <div className="flex justify-between">
              <span>Account Status</span>
              <span className="text-emerald-600 font-semibold">Active</span>
            </div>
            <div className="flex justify-between">
              <span>Member Since</span>
              <span>{user?.created_at ? new Date(user.created_at).toLocaleDateString("en-IN") : "N/A"}</span>
            </div>
            <div className="flex justify-between">
              <span>Role</span>
              <span className="capitalize font-semibold text-brand-500">{user?.role}</span>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
